import { FC, memo, useEffect } from 'react'

import { useFocusNodeById } from '@please/lrud'
import { Element } from 'react-scroll'

import { useTabPointerNavigation } from '@/components/TabsNav/useTabPointerNavigation'
import { scrollOptions } from '@/constants/scrollers'
import { useCustomFocus } from '@/hooks/useCustomFocus'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { TabProps } from '@/types/componentsPropsTypes'
import { TabType } from '@/types/servicesTypes/pageTypes'
import { isPointerMode, showInfo } from '@/utils/utils'

import {
  NextNavigationSection,
  PrevNavigationSection,
  TabElem,
  TabsElem,
  TabsElemWrapper,
} from './styles'

type TabNavProps = {
  focusIdPrefix: string
  pageId: string
  tabs: Array<TabType>
  tabIndex: number
  activeTab?: TabType
  setActiveTab: (value: TabType, index: number) => void
}

const TabsNavRaw: FC<TabNavProps> = ({
  focusIdPrefix,
  tabs,
  tabIndex,
  activeTab,
  setActiveTab,
}) => {
  const focusId = `${focusIdPrefix}tabsElem`
  const tabContainerFocusNode = useFocusNodeById(focusId)
  const { t } = useCommonTranslation()

  const {
    defaultFocusChild,
    setDefaultFocusChild,
    onMove,
    onSelectWithScroll,
  } = useCustomFocus({
    focusId,
    scrollOptions: scrollOptions.TabScroll.tabsNav,
    startFocusChild: tabContainerFocusNode?.isFocused ? tabIndex : undefined,
    resetStartFocusChild: true,
  })

  const {
    canScrollPrev,
    canScrollNext,
    stopTabNavigation,
    forwardTabNavigation,
    backwardTabNavigation,
  } = useTabPointerNavigation({
    tabIndex,
    focusNode: tabContainerFocusNode,
    setDefaultFocusChild,
  })

  useEffect(() => {
    if (defaultFocusChild !== tabIndex && !tabContainerFocusNode?.isFocused) {
      const focusId = `${focusIdPrefix}${tabs[tabIndex]?.title}${tabIndex}`
      onSelectWithScroll(focusId, tabIndex)
    }
  }, [tabIndex, tabContainerFocusNode?.isFocused])

  /**
   * fallback to first tab if tabIndex is out of range
   */
  useEffect(() => {
    if (tabIndex > tabs.length - 1) {
      setActiveTab(tabs[0], 0)
      showInfo(t('contentUnavailable'))
    }
  }, [tabIndex])

  const onSelected = (tab: TabType, index: number) => () => {
    setActiveTab(tab, index)
  }

  const tabContainerElement = tabContainerFocusNode?.elRef.current
  const tabContainerWidth = tabContainerElement?.offsetWidth || 0
  const totalTabsWidth = Array.from(tabContainerElement?.children || []).reduce(
    (totalWidth, tab) => totalWidth + (tab as HTMLElement).offsetWidth,
    0,
  )

  const isTabsOverflow = totalTabsWidth > tabContainerWidth
  const isTabContainerFocused = Boolean(tabContainerFocusNode?.isFocused)
  const showPrevNavigationSection = isTabContainerFocused && canScrollPrev
  const showNextNavigationSection = isTabContainerFocused && canScrollNext

  return (
    <TabsElemWrapper
      $showPrevNavigation={showPrevNavigationSection}
      $showNextNavigation={showNextNavigationSection}
    >
      <TabsElem
        elementType="section"
        id={scrollOptions.TabScroll.tabsNav.containerId}
        focusId={focusId}
        defaultFocusChild={defaultFocusChild}
        onMove={onMove}
      >
        {tabs.map((tab, index) => {
          const focusId = `${focusIdPrefix}${tab.title}${index}`
          return (
            <Element key={`tab${focusId}`} name={focusId}>
              <Tab
                isActiveTab={activeTab === tab}
                onSelected={onSelected(tab, index)}
                focusId={focusId}
                title={tab.title}
              />
            </Element>
          )
        })}
      </TabsElem>
      {isPointerMode && isTabsOverflow && (
        <>
          <PrevNavigationSection
            onMouseEnter={backwardTabNavigation}
            onMouseOut={stopTabNavigation}
          />
          <NextNavigationSection
            onMouseEnter={forwardTabNavigation}
            onMouseOut={stopTabNavigation}
          />
        </>
      )}
    </TabsElemWrapper>
  )
}

export const TabsNav = memo(TabsNavRaw)

const TabRaw: FC<TabProps> = ({ onSelected, focusId, title, isActiveTab }) => {
  return (
    <TabElem
      elementType="div"
      focusId={focusId}
      onSelected={onSelected}
      $activeTab={isActiveTab}
    >
      {title}
    </TabElem>
  )
}

const Tab = memo(TabRaw)
